<template>
  <div class="uv-list">
    <div v-if="!selectionStore.selectedMeshId" class="text-sm text-gray-500">
      No mesh selected. Select a mesh to view its UV channels.
    </div>
    <div v-else-if="uvData.length === 0" class="text-sm text-gray-500">No UV data found for this mesh.</div>
    <div v-else class="space-y-2">
      <div class="mb-2 text-sm font-medium text-gray-300">
        {{ uvData[0]?.name || 'Selected Mesh' }}
      </div>

      <div v-if="uvData[0]?.uvNames && uvData[0].uvNames.length > 0" class="space-y-2">
        <div
          v-for="uvName in uvData[0].uvNames"
          :key="uvName"
          class="flex items-center justify-between rounded border border-gray-600 p-2">
          <span class="text-sm text-gray-300">{{ uvName }}</span>
          <div class="flex items-center space-x-2">
            <span v-if="getUVAsset(uvData[0].name, uvName)" class="text-xs text-gray-400">
              {{ getUVAsset(uvData[0].name, uvName) }}
            </span>
            <DButton
              v-if="getUVAsset(uvData[0].name, uvName)"
              text
              size="small"
              class="p-1.5 text-gray-500 hover:text-red-400"
              @click="removeAsset(uvData[0].name, uvName)">
              <Trash class="h-4 w-4" />
            </DButton>
            <DButton v-else size="small" @click="changeAsset(uvData[0].name, uvName)"> Select Asset </DButton>
          </div>
        </div>
      </div>
      <div v-else class="rounded border border-gray-600 p-2">
        <DButton size="small" @click="changeAsset(uvData[0].name, 'uv')"> Select asset (default UV) </DButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useNodeManager } from '@/composables/useNodeManager';
import { useSelectionStore } from '@/stores/ui/selection';
import { getObjectByCode, getObjectByName } from '@/threejs/objectManager';
import { DButton } from '@disguise/cloud-ui';
import { Trash } from '@disguise/cloud-ui/icons';
import { defineAsyncComponent, ref, watch } from 'vue';

const DriveSelector = defineAsyncComponent(() => import('@/components/dialogs/DriveSelector.vue'));

import { useDialog } from '@disguise/cloud-ui';
const dialog = useDialog();
const { findNodeById, updateNode } = useNodeManager();
const selectionStore = useSelectionStore();
const uvData = ref([]);

function getUVNamesFromObject(object) {
  const uvNames = [];

  // Check if object has geometry with UV attributes
  if (object.geometry && object.geometry.attributes) {
    const attributes = object.geometry.attributes;

    // Look for UV-related attributes
    Object.keys(attributes).forEach((key) => {
      if (key.toLowerCase().includes('uv')) {
        uvNames.push(key);
      }
    });
  }

  return uvNames;
}

const getUVAsset = (itemName, uvName) => {
  const nodeId = selectionStore.selectedMeshId || selectionStore.selectedObjectId;
  const node = findNodeById(nodeId);
  if (!node || !node.uvAssets) return null;

  // Search through uvAssets array for an object that has uvName as a key
  for (const assetObj of node.uvAssets) {
    if (assetObj[itemName]) {
      return assetObj[itemName].resource_id;
    }
  }
  return null;
};

function updateUVList() {
  uvData.value = [];

  // Only show UV data if a specific mesh is selected
  if (selectionStore.selectedMeshId) {
    const meshObject = getObjectByCode(selectionStore.selectedMeshId);
    if (meshObject) {
      const uvNames = getUVNamesFromObject(meshObject);
      uvData.value = [
        {
          objectId: meshObject.userData.uuid || 'unknown',
          name: meshObject.userData.name || meshObject.name || meshObject.type,
          uvNames: uvNames,
        },
      ];
    }
  }
}

// Watch for changes in selected mesh only
watch(
  () => selectionStore.selectedMeshId,
  () => {
    updateUVList();
  },
  { immediate: true }
);

function changeAsset(objectName, uvChannelName = 'uv') {
  dialog.open(DriveSelector, {
    data: {
      filter: 'media',
    },
    props: {
      header: 'Asset List',
      style: {
        width: '50vw',
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      modal: true,
    },
    onClose: async (options) => {
      const object = getObjectByName(objectName);
      if (!object || !object.material) return;

      if (options.data?.data?.id) {
        const targetNodeId = selectionStore.selectedMeshId || selectionStore.selectedObjectId;
        const node = findNodeById(targetNodeId);

        const uvAssets = node.uvAssets || [];

        // Check if objectName already exists in uvAssets array
        let found = false;
        for (const assetObj of uvAssets) {
          if (assetObj[objectName]) {
            // Update existing resource_id
            assetObj[objectName].resource_id = options.data.data.id;
            assetObj[objectName].uv_channel = uvChannelName;
            found = true;
            break;
          }
        }

        // If not found, add new entry
        if (!found) {
          uvAssets.push({
            [objectName]: {
              resource_id: options.data.data.id,
              uv_channel: uvChannelName,
            },
          });
        }

        updateNode(targetNodeId, { uvAssets });
      }
    },
  });
}

function removeAsset(objectName, uvChannelName = 'uv') {
  const object = getObjectByName(objectName);
  if (!object || !object.material) return;

  const nodeId = selectionStore.selectedMeshId || selectionStore.selectedObjectId;
  const node = findNodeById(nodeId);
  if (!node || !node.uvAssets) return;

  const uvAssets = node.uvAssets || [];

  // Remove the entry for the specified objectName
  const updatedUvAssets = uvAssets.filter((assetObj) => !assetObj.hasOwnProperty(objectName));

  updateNode(nodeId, { uvAssets: updatedUvAssets });
}
</script>
